// @flow
import React, { Component } from "react";
import classNames from "classnames";
import withStyles from "elevate-ui/withStyles";
import Paper from "elevate-ui/Paper";
import assign from "lodash/assign";
import isEqual from "lodash/isEqual";
import reduce from "lodash/reduce";
import cloneDeep from "lodash/cloneDeep";
import { Map, FeatureGroup, TileLayer, Polygon } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import { withGlobalContext } from "../common/GlobalContext";
import axios from "axios";
import StyledMarker from "../common/StyledMarker";
import type { CompliantListingType } from "flow/types";
type Props = {
  classes: Object,
  className?: string,
  defaultZoom: number,
  handleSearch: (null | Event, Object, null | boolean) => void,
  listingSearchLoading: boolean,
  mapProvider: Object,
  mapTypeId: string,
  markers: Object,
  query: Object,
  scrollWheelZoom: boolean,
  statusOverride: string,
};

type State = {
  activeMarker: number | null,
  activeListing: CompliantListingType | null,
  favorites: Array<number>,
};

class ListingMap extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      activeMarker: null,
      activeListing: null,
      favorites: [],
    };
  }

  // make flow happy when using a ref or `this`
  listingMap: any;

  handleClick = (marker) => {
    this.setState({ activeListing: null });
    axios
      .get(`/api/info-window/${marker.id}?format=json`)
      .then(({ data: { favorites, listing } }) => {
        this.setState({
          favorites,
          activeListing: listing,
          activeMarker: marker.id,
        });
      });
  };

  getLatLngPoints() {
    if (!(this.props.markers && this.props.markers.length)) {
      return [];
    }
    let mapBounds = [];
    this.props.markers.forEach((marker) => {
      if (marker.lat && marker.lng) {
        mapBounds.push([marker.lat, marker.lng]);
      }
    });
    return mapBounds;
  }

  setMapBounds = (listingMap) => {
    const bounds = this.getLatLngPoints();
    if (bounds && bounds.length) {
      if (bounds.length > 1) {
        listingMap.leafletElement.fitBounds(bounds);
      } else {
        listingMap.leafletElement.setView(bounds[0]);
      }
    }
  };

  componentDidMount() {
    this.setMapBounds(this.listingMap);
  }

  componentDidUpdate(prevProps: Props) {
    if (!isEqual(prevProps.markers, this.props.markers)) {
      this.setMapBounds(this.listingMap);
    }
  }

  render() {
    const {
      className,
      classes,
      defaultZoom,
      mapProvider,
      mapTypeId,
      markers,
      scrollWheelZoom,
      query,
    } = this.props;

    const { activeListing, favorites } = this.state;
    const bounds = this.getLatLngPoints();
    const center =
      this.listingMap && this.listingMap.getLatLngBounds
        ? new this.listingMap.getLatLngBounds(bounds)
        : [41.850033, -87.6500523];

    return (
      <div className={classNames(classes.container, className)}>
        <Paper>
          <Map
            id="listingsMap"
            ref={(node) => (this.listingMap = node)}
            animate={true}
            zoom={defaultZoom}
            center={center}
            scrollWheelZoom={scrollWheelZoom}
            maxZoom={mapProvider.maxZoom}
            style={{ width: "100%", height: "540px", zIndex: 0 }}
          >
            <FeatureGroupMap
              query={query}
              handleSearch={(e, values, preventDebounce) =>
                this.props.handleSearch(e, values, preventDebounce)
              }
            />
            <TileLayer
              attribution={mapProvider.attribution}
              url={mapProvider.url}
              maxZoom={mapProvider.maxZoom}
              id={mapTypeId}
            />
            {markers &&
              markers.length > 0 &&
              markers.map(
                (marker) =>
                  marker.lat && marker.lng ? (
                    <StyledMarker
                      onClick={() => this.handleClick(marker)}
                      markerId={marker.id}
                      key={marker.id}
                      status={marker.status}
                      position={[
                        parseFloat(marker.lat),
                        parseFloat(marker.lng),
                      ]}
                      favorites={favorites}
                      label={marker.label}
                      classes={classes}
                      activeListing={activeListing}
                    />
                  ) : null
              )}
          </Map>
        </Paper>
      </div>
    );
  }
}

type FGProps = {
  query: Object,
  handleSearch: (null | Event, Object, null | boolean) => void,
};

type FGState = {};

/*
  Need to move this to a separate component because modifying the map shapes (create/edit/delete)
  happens on this layer so will not need to rerender component.
  Rerendering component is a really messy process that cause issues in https://code.knledg.com/elm/sites/issues/199
*/
class FeatureGroupMap extends Component<FGProps, FGState> {
  shouldComponentUpdate(nextProps: Object) {
    //clear all filter occured - clear layer
    if (!this.props.query.geoJson && !nextProps.query.geoJson) {
      const reactFGref = this.refs.reactFGref;

      if (reactFGref) {
        const layerContainer = reactFGref.context.layerContainer;
        const layers = layerContainer._layers;
        const layer_ids = Object.keys(layers);
        layer_ids.forEach((id) => {
          const layer = layers[id];
          if (layer && Array.isArray(layer._latlngs)) {
            layerContainer.removeLayer(layer);
          }
        });
      }
    }

    return false;
  }

  /**
   * Array of features
   * @param {*} geoJSON
   */
  convertPolygonsToMultipolygon(geoJSON: Array<Object>) {
    const coordinates = reduce(
      geoJSON,
      (result, feature) => {
        const coords = reduce(
          feature._latlngs[0],
          (output, coord) => {
            if (coord.lng && coord.lat) {
              output.push([coord.lng, coord.lat]);
            }
            return output;
          },
          []
        );
        if (coords.length) {
          result.push([coords]);
        }
        return result;
      },
      []
    );
    if (coordinates.length) {
      return JSON.stringify({
        type: "MultiPolygon",
        coordinates,
      });
    } else {
      return null;
    }
  }

  handleDrawCreate = (e) => {
    const shapeLayer: Array<any> = this.getShapes();

    // for some reason shape gets added twice as a layer so need only unique polygons
    // since new layer is added at the end it's safe to shift the array and remove the first array which contains `undefined` values

    return this.props.handleSearch(
      e,
      assign({}, this.props.query, {
        geoJson: this.convertPolygonsToMultipolygon(shapeLayer),
      }),
      true
    );
  };

  handleShapeEditDelete = (e) => {
    const shapeLayer = this.getShapes();

    return this.props.handleSearch(
      e,
      assign({}, this.props.query, {
        geoJson: this.convertPolygonsToMultipolygon(shapeLayer),
      }),
      true
    );
  };

  getShapes = () => {
    const reactFGref = this.refs.reactFGref;
    const shapeLayer = [];

    if (reactFGref) {
      const mapShapes = Object.values(
        reactFGref.context.layerContainer._layers
      ).filter((layer) =>
        // $FlowIgnore
        Array.isArray(layer._latlngs)
      );

      mapShapes.forEach((shape) => {
        const coordsArray = [];
        // $FlowIgnore
        shape.toGeoJSON().geometry.coordinates[0].forEach((coord) => {
          coordsArray.push({ lat: coord[1], lng: coord[0] });
        });

        const modifiedLayer = cloneDeep(shape);
        // $FlowIgnore
        modifiedLayer._latlngs = [coordsArray];

        shapeLayer.push(modifiedLayer);
      });
    }

    return shapeLayer;
  };

  render() {
    const { query } = this.props;

    const polygonsToRender = [];
    if (query.geoJson) {
      const multipolygon = JSON.parse(query.geoJson);
      multipolygon.coordinates.forEach((polygon) => {
        const coordsArray = [];
        polygon[0].forEach((coord) => {
          var latLng = coord.reverse();
          coordsArray.push(latLng);
        });
        polygonsToRender.push(coordsArray);
      });
    }
    return (
      <FeatureGroup ref="reactFGref">
        <EditControl
          position="topleft"
          onEdited={(e) => this.handleShapeEditDelete(e)}
          onCreated={(e) => this.handleDrawCreate(e)}
          onDeleted={(e) => this.handleShapeEditDelete(e)}
          draw={{
            polyline: false,
            circle: false,
            marker: false,
            circlemarker: false,
            edit: false,
            remove: false,
          }}
        />
        {polygonsToRender.map((polygon, idx) => {
          return <Polygon positions={polygon} key={idx} />;
        })}
      </FeatureGroup>
    );
  }
}

export default withStyles((theme) => ({
  root: {
    width: "100%",
    maxWidth: "1172px",
    height: "620px",
    margin: "50px auto",
  },
  mapPin: {
    position: "absolute",
    zIndex: theme.zIndex["mapPin"],
    cursor: "pointer",

    // white circle
    "&:before": {
      content: "''",
      position: "absolute",
      top: 0,
      left: 0,
      display: "block",
      width: "16px",
      height: "16px",
      margin: "-2px", // We're given a 12px box to work with– since we set this to be 16px, we need to adjust it to re-center it
      boxShadow: theme.globalBoxShadow,
      borderRadius: "50%",
      background: "white",
    },

    // primary color circle
    "&::after": {
      content: "''",
      position: "absolute",
      top: "0",
      left: "0",
      display: "block",
      width: "12px",
      height: "12px",
      boxShadow: "inset 0 0 1px 0 rgba(0, 0 , 0, 0.3)",
      borderRadius: "50%",
      background: theme.colors.green["500"],
    },

    "&:hover": {
      zIndex: theme.zIndex["mapPinHover"],
    },
  },
  priceLabel: {
    position: "absolute",
    top: "100%",
    left: "50%",
    zIndex: theme.zIndex.tableLoading,
    marginTop: theme.spacing.unit * 0.5,
    padding: "4px 8px",
    transform: "translateX(-50%)",
    boxShadow: theme.globalBoxShadow,
    borderRadius: "3px",
    background: "white",
    fontSize: "12px",
    lineHeight: "12px",
    fontWeight: "600",
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  popup: {
    borderRadius: theme.globalBorderRadius,
    boxShadow: theme.globalBoxShadow,
    overflow: "hidden",
    "& .leaflet-popup-close-button": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      top: "4px !important",
      right: "7px !important",
      width: "25px !important",
      height: "25px !important",
      color: `${theme.colors.white} !important`,
      font: "inherit !important",
      fontSize: "25px !important",
      fontWeight: "700 !important",
      padding: "4px !important",
      borderRadius: "50% !important",
      background: `${theme.colors.gray["100"]} !important`,
    },
  },
  container: {
    margin: "18px auto 40px",
  },
  loader: {
    margin: "15px auto",
    textAlign: "center",
  },
}))(withGlobalContext(ListingMap));
